import { useEffect, useState } from 'react';

import { EXPERIMENTS, EXPERIMENTS_DECISIONS, EXPERIMENTS_RULE_KEYS } from 'Services/optimizely/optimizely.consts';
import { useOptimizelyDecisionWithCustomUserId } from 'Services/optimizely/useOptimizelyDecisionWithCustomUserId';
import useFeatureToggle, { FEATURES } from 'Utilities/hooks/useFeatureToggle';
import useUser from 'Utilities/hooks/useUser';

import { useOptimizely } from '../useOptimizely';

/**
 * Reads two LaunchDarkly flags (`proInfoHierarchyNewVariant` and `proInfoHierarchyOldVariant`)
 * to determine which cohort this dealer is in, then forwards the decision to
 * Optimizely's `pro_info_hierarchy` flag for measurement.
 *
 * - `proInfoHierarchyNewVariant` true  → show new vehicle list, force "show_new_vehicle_list"
 * - `proInfoHierarchyOldVariant` true  → show old vehicle list, force "show_old_vehicle_list"
 * - Neither true              → not in experiment, show old vehicle list, skip measurement
 *
 * This hook is the single source of truth for whether to render the new or old
 * vehicle list. Consumers should use `shouldShowNewVehicleList` from this hook
 * rather than calling `useFeatureToggle` directly.
 *
 * Reference: useUpdatedGradingExperimentation (782ea534)
 */
export const useProInfoHierarchyExperimentation = () => {
	const isInNewCohort = useFeatureToggle(FEATURES.proInfoHierarchyNewVariant);
	const isInOldCohort = useFeatureToggle(FEATURES.proInfoHierarchyOldVariant);
	const optimizelyClient = useOptimizely();
	const { dealerId } = useUser();
	const [isReadyToMeasure, setIsReadyToMeasure] = useState(false);

	const isInExperiment = isInNewCohort || isInOldCohort;

	const forcedVariation = isInNewCohort
		? EXPERIMENTS_DECISIONS.PRO_INFO_HIERARCHY.SHOW_NEW_VEHICLE_LIST
		: EXPERIMENTS_DECISIONS.PRO_INFO_HIERARCHY.SHOW_OLD_VEHICLE_LIST;

	useEffect(() => {
		if (optimizelyClient && dealerId && isInExperiment) {
			optimizelyClient.setForcedVariation(EXPERIMENTS_RULE_KEYS.PRO_INFO_HIERARCHY, `${dealerId}`, forcedVariation);
			setIsReadyToMeasure(true);
		}
	}, [optimizelyClient, dealerId, forcedVariation, isInExperiment]);

	// Measuring decision — only fire for dealers in the experiment
	useOptimizelyDecisionWithCustomUserId({
		attributes: {
			dealership_id: dealerId,
			isReadyToMeasure: isInExperiment && isReadyToMeasure,
		},
		experimentId: EXPERIMENTS.PRO_INFO_HIERARCHY,
		userId: dealerId,
	});

	return { shouldShowNewVehicleList: !!isInNewCohort };
};
